/**
 * E-commerce Demo Page
 * 
 * Comprehensive demonstration of the e-commerce functionality including
 * product catalog, shopping cart, and checkout flow with payment processing.
 */

import React, { useState, useEffect } from 'react';
import { ProductGrid } from '../components/ecommerce/ProductGrid';
import { ShoppingCart } from '../components/ecommerce/ShoppingCart';
import { Checkout } from '../components/ecommerce/Checkout';
import { PaymentGateway, PaymentResult } from '../components/ecommerce/PaymentGateway';
import { useCartStore, cartSelectors } from '../store/simpleCartStore';
import { Product } from '../types/ecommerce';
import { Button } from '../components/Button';

/**
 * Demo view types
 */
type DemoView = 'products' | 'cart' | 'checkout' | 'payment' | 'success';

/**
 * Mock product data for demonstration
 */
const DEMO_PRODUCTS: Product[] = [
  {
    id: '1',
    name: 'Premium Wireless Headphones',
    description: 'High-quality wireless headphones with noise cancellation and premium sound quality.',
    shortDescription: 'Premium wireless headphones with noise cancellation',
    price: 199.99,
    regularPrice: 249.99,
    salePrice: 199.99,
    images: [
      {
        id: '1',
        src: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=500',
        alt: 'Premium Wireless Headphones',
        name: 'headphones-main'
      }
    ],
    categories: [
      { id: '1', name: 'Electronics', slug: 'electronics' },
      { id: '2', name: 'Audio', slug: 'audio' }
    ],
    inventory: {
      stockQuantity: 50,
      stockStatus: 'instock',
      manageStock: true,
      backordersAllowed: false
    },
    seo: {
      metaTitle: 'Premium Wireless Headphones',
      metaDescription: 'High-quality wireless headphones with noise cancellation'
    },
    sku: 'PWH-001',
    weight: '0.8',
    status: 'publish',
    type: 'simple',
    featured: true,
    dateCreated: new Date(),
    dateModified: new Date()
  },
  {
    id: '2',
    name: 'Smart Fitness Watch',
    description: 'Advanced fitness tracking with heart rate monitoring, GPS, and smartphone integration.',
    shortDescription: 'Advanced fitness tracking smartwatch',
    price: 299.99,
    images: [
      {
        id: '2',
        src: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=500',
        alt: 'Smart Fitness Watch',
        name: 'watch-main'
      }
    ],
    categories: [
      { id: '1', name: 'Electronics', slug: 'electronics' },
      { id: '3', name: 'Fitness', slug: 'fitness' }
    ],
    inventory: {
      stockQuantity: 30,
      stockStatus: 'instock',
      manageStock: true,
      backordersAllowed: false
    },
    seo: {
      metaTitle: 'Smart Fitness Watch',
      metaDescription: 'Advanced fitness tracking smartwatch'
    },
    sku: 'SFW-002',
    weight: '0.1',
    status: 'publish',
    type: 'simple',
    featured: false,
    dateCreated: new Date(),
    dateModified: new Date()
  },
  {
    id: '3',
    name: 'Portable Bluetooth Speaker',
    description: 'Compact wireless speaker with powerful sound and long battery life. Perfect for outdoor adventures.',
    shortDescription: 'Compact wireless speaker with powerful sound',
    price: 79.99,
    regularPrice: 99.99,
    salePrice: 79.99,
    images: [
      {
        id: '3',
        src: 'https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=500',
        alt: 'Portable Bluetooth Speaker',
        name: 'speaker-main'
      }
    ],
    categories: [
      { id: '1', name: 'Electronics', slug: 'electronics' },
      { id: '2', name: 'Audio', slug: 'audio' }
    ],
    inventory: {
      stockQuantity: 75,
      stockStatus: 'instock',
      manageStock: true,
      backordersAllowed: false
    },
    seo: {
      metaTitle: 'Portable Bluetooth Speaker',
      metaDescription: 'Compact wireless speaker with powerful sound'
    },
    sku: 'PBS-003',
    weight: '0.5',
    status: 'publish',
    type: 'simple',
    featured: true,
    dateCreated: new Date(),
    dateModified: new Date()
  },
  {
    id: '4',
    name: 'Wireless Charging Pad',
    description: 'Fast wireless charging pad compatible with all Qi-enabled devices. Sleek design for home or office.',
    shortDescription: 'Fast wireless charging pad for Qi devices',
    price: 39.99,
    images: [
      {
        id: '4',
        src: 'https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?w=500',
        alt: 'Wireless Charging Pad',
        name: 'charger-main'
      }
    ],
    categories: [
      { id: '1', name: 'Electronics', slug: 'electronics' },
      { id: '4', name: 'Accessories', slug: 'accessories' }
    ],
    inventory: {
      stockQuantity: 100,
      stockStatus: 'instock',
      manageStock: true,
      backordersAllowed: false
    },
    seo: {
      metaTitle: 'Wireless Charging Pad',
      metaDescription: 'Fast wireless charging pad for Qi devices'
    },
    sku: 'WCP-004',
    weight: '0.3',
    status: 'publish',
    type: 'simple',
    featured: false,
    dateCreated: new Date(),
    dateModified: new Date()
  }
];

/**
 * E-commerce Demo Component
 */
export const EcommerceDemo: React.FC = () => {
  const [currentView, setCurrentView] = useState<DemoView>('products');
  const [demoProducts, setDemoProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentAmount, setPaymentAmount] = useState(0);

  // Cart state
  const items = useCartStore(cartSelectors.items);
  const itemCount = useCartStore(cartSelectors.itemCount);
  const clearCart = useCartStore((state) => state.clearCart);
  const calculateTotals = useCartStore((state) => state.calculateTotals);

  /**
   * Initialize demo data
   */
  useEffect(() => {
    // Simulate loading products
    const loadDemoProducts = async () => {
      setIsLoading(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setDemoProducts(DEMO_PRODUCTS);
      setIsLoading(false);
    };

    loadDemoProducts();
  }, []);

  /**
   * Calculate cart total for payment
   */
  useEffect(() => {
    if (demoProducts.length > 0) {
      const productsMap = demoProducts.reduce((acc, product) => {
        acc[product.id] = product;
        return acc;
      }, {} as Record<string, Product>);
      
      calculateTotals(productsMap);
      
      let total = 0;
      items.forEach(item => {
        const product = demoProducts.find(p => p.id === item.productId);
        if (product) {
          total += product.price * item.quantity;
        }
      });
      setPaymentAmount(Math.round(total * 100)); // Convert to cents for Stripe
    }
  }, [items, demoProducts, calculateTotals]);

  /**
   * Handle checkout completion
   */
  const handleCheckoutComplete = () => {
    setCurrentView('payment');
  };

  /**
   * Handle payment success
   */
  const handlePaymentSuccess = (result: PaymentResult) => {
    console.log('Payment successful:', result);
    setCurrentView('success');
  };

  /**
   * Handle payment error
   */
  const handlePaymentError = (error: string) => {
    console.error('Payment failed:', error);
    alert(`Payment failed: ${error}`);
  };

  /**
   * Reset demo to start over
   */
  const handleResetDemo = async () => {
    await clearCart();
    setCurrentView('products');
  };

  /**
   * Navigation helpers
   */
  const goToCart = () => setCurrentView('cart');
  const goToCheckout = () => setCurrentView('checkout');
  const goToProducts = () => setCurrentView('products');

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Demo Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">E-commerce Demo</h1>
              <p className="text-gray-600 mt-1">
                Complete shopping experience with cart, checkout, and payment processing
              </p>
            </div>
            
            {/* Cart indicator */}
            {itemCount > 0 && (
              <button
                onClick={goToCart}
                className="relative bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                🛒 Cart ({itemCount})
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                  {itemCount}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Demo Navigation */}
      <div className="bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex space-x-6">
            <button
              onClick={goToProducts}
              className={`px-3 py-1 rounded ${
                currentView === 'products' ? 'bg-blue-700' : 'hover:bg-blue-500'
              }`}
            >
              1. Products
            </button>
            <button
              onClick={goToCart}
              className={`px-3 py-1 rounded ${
                currentView === 'cart' ? 'bg-blue-700' : 'hover:bg-blue-500'
              }`}
              disabled={itemCount === 0}
            >
              2. Cart {itemCount > 0 && `(${itemCount})`}
            </button>
            <button
              onClick={goToCheckout}
              className={`px-3 py-1 rounded ${
                currentView === 'checkout' ? 'bg-blue-700' : 'hover:bg-blue-500'
              }`}
              disabled={itemCount === 0}
            >
              3. Checkout
            </button>
            <span
              className={`px-3 py-1 rounded ${
                currentView === 'payment' ? 'bg-blue-700' : 'text-blue-200'
              }`}
            >
              4. Payment
            </span>
            <span
              className={`px-3 py-1 rounded ${
                currentView === 'success' ? 'bg-blue-700' : 'text-blue-200'
              }`}
            >
              5. Success
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Products View */}
        {currentView === 'products' && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Product Catalog</h2>
              <p className="text-gray-600">
                Browse our demo products and add them to your cart to test the full e-commerce flow.
              </p>
            </div>
            
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading products...</p>
                </div>
              </div>
            ) : (
              <ProductGrid
                products={demoProducts}
                loading={false}
                error={null}
                onLoadMore={async () => {}}
                showFilters={true}
                showSort={true}
                layout="grid"
                columns={4}
                virtualized={false}
                infiniteScroll={false}
              />
            )}
          </div>
        )}

        {/* Cart View */}
        {currentView === 'cart' && (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Shopping Cart</h2>
                <p className="text-gray-600">
                  Review your items and proceed to checkout when ready.
                </p>
              </div>
              <Button onClick={goToProducts} variant="outline">
                Continue Shopping
              </Button>
            </div>
            
            <ShoppingCart
              variant="page"
              onCheckout={goToCheckout}
              onClose={goToProducts}
              products={demoProducts.reduce((acc, product) => {
                acc[product.id] = product;
                return acc;
              }, {} as Record<string, Product>)}
            />
          </div>
        )}

        {/* Checkout View */}
        {currentView === 'checkout' && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Checkout</h2>
              <p className="text-gray-600">
                Complete your order with shipping and billing information.
              </p>
            </div>
            
            <Checkout
              onComplete={handleCheckoutComplete}
              onCancel={goToCart}
              products={demoProducts.reduce((acc, product) => {
                acc[product.id] = product;
                return acc;
              }, {} as Record<string, Product>)}
            />
          </div>
        )}

        {/* Payment View */}
        {currentView === 'payment' && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Processing</h2>
              <p className="text-gray-600">
                Choose your payment method and complete the transaction.
              </p>
            </div>
            
            <PaymentGateway
              amount={paymentAmount}
              currency="USD"
              availableMethod={['stripe', 'paypal']}
              items={items.map(item => {
                const product = demoProducts.find(p => p.id === item.productId);
                return {
                  name: product?.name || 'Unknown Product',
                  price: product?.price || 0,
                  quantity: item.quantity,
                  category: 'PHYSICAL_GOODS' as const
                };
              })}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              onCancel={() => setCurrentView('checkout')}
              customerInfo={{
                email: 'demo@example.com',
                name: 'Demo Customer'
              }}
            />
          </div>
        )}

        {/* Success View */}
        {currentView === 'success' && (
          <div className="text-center py-12">
            <div className="text-green-600 text-6xl mb-6">✅</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Demo Complete!</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              You've successfully completed the full e-commerce flow from product selection 
              to payment processing. This demonstrates all the key components working together.
            </p>
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">What you tested:</h3>
              <ul className="text-gray-600 space-y-2 max-w-md mx-auto">
                <li>✅ Product catalog with filtering and sorting</li>
                <li>✅ Shopping cart with quantity management</li>
                <li>✅ Multi-step checkout process</li>
                <li>✅ Payment gateway integration (Stripe & PayPal)</li>
                <li>✅ Order confirmation and success handling</li>
              </ul>
            </div>
            
            <Button
              onClick={handleResetDemo}
              variant="primary"
              size="lg"
              className="mt-8"
            >
              Start Demo Again
            </Button>
          </div>
        )}
      </div>

      {/* Demo Instructions */}
      {currentView === 'products' && (
        <div className="bg-blue-50 border-t border-blue-200">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">Demo Instructions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-blue-800">
              <div>
                <strong>Step 1:</strong> Browse products and click "Add to Cart" to add items
              </div>
              <div>
                <strong>Step 2:</strong> View your cart, adjust quantities, and proceed to checkout
              </div>
              <div>
                <strong>Step 3:</strong> Fill out shipping information and continue to payment
              </div>
              <div>
                <strong>Step 4:</strong> Test payment with mock Stripe or PayPal integration
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EcommerceDemo;